import type {
  ChatSnapshot,
  ChatMessage,
  Conn,
  Part,
} from 'src/components/agentic-ui';
import {createStore as createZustandStore} from 'zustand/vanilla';


export type ChatState = ChatSnapshot & {
  setConn: (c: Conn) => void;
  setErr: (e: ChatSnapshot['error']) => void;
  clear: () => void;
  hydrate: (msgs: ChatMessage[], strat?: 'merge' | 'replace') => void;
  insertMessage: (message: ChatMessage) => void;
  insertMessagePart: (messageId: string, messagePart: Part) => void;
  updateMessagePart: (messageId: string, messagePart: Part) => void;
  updateMessagePartTextDelta: (
    messageId: string,
    messagePartId: string,
    textDelta: string,
  ) => void;
  ack: (clientId: string, serverId?: string) => void;
  replaceUserMessage: (message: ChatMessage) => void;
  removeMessagePart: (messageId: string, partType: string) => void;
  markError: (clientId: string) => void;
  addLocal: (local: ChatMessage) => void;
};

export function createStore(initial?: Partial<ChatSnapshot>) {
  const store = createZustandStore<ChatState>((set, get) => ({
    messages: initial?.messages ?? [],
    error: null,
    connectionStatus: 'CLOSED',
    replaceUserMessage: (message: ChatMessage) =>
      set((s) => ({
        messages: [...s.messages.slice(0, -1), message],
      })),
    setConn: (c: Conn) => set({connectionStatus: c}),
    setErr: (e: ChatSnapshot['error']) => set({error: e}),
    clear: () => set({messages: [], error: null, connectionStatus: 'CLOSED'}),
    hydrate: (msgs: ChatMessage[], strat: 'merge' | 'replace' = 'merge') => {
      if (strat === 'replace') {
        set({messages: msgs});
        return;
      }
      const current = get();
      const map = new Map(current.messages.map((m) => [m.id, m]));
      for (const m of msgs) {
        map.set(m.id, m);
      }
      set({
        messages: [...map.values()].sort((a, b) => a.createdAt - b.createdAt),
      });
    },
    insertMessage: (message: ChatMessage) =>
      set((s) => ({messages: [...s.messages, message]})),
    insertMessagePart: (messageId: string, messagePart: Part) =>
      set((s) => ({
        messages: s.messages.map((m) =>
          m.id === messageId ? {...m, parts: [...m.parts, messagePart]} : m,
        ),
      })),
    updateMessagePart: (messageId: string, messagePart: Part) =>
      set((s) => ({
        messages: s.messages.map((m) =>
          m.id === messageId
            ? {
                ...m,
                parts: m.parts.map((p) =>
                  p.type === messagePart.type ? messagePart : p,
                ),
              }
            : m,
        ),
      })),
    updateMessagePartTextDelta: (
      messageId: string,
      messagePartId: string,
      textDelta: string,
    ) =>
      set((s) => ({
        messages: s.messages.map((m) =>
          m.id === messageId
            ? {
                ...m,
                parts: m.parts.map((p) =>
                  p.id === messagePartId ? {...p, text: textDelta} : p,
                ),
              }
            : m,
        ),
      })),
    ack: (clientId: string, serverId?: string) =>
      set((s) => ({
        messages: s.messages.map((m) =>
          m.id === clientId ? {...m, serverId, status: 'sent'} : m,
        ),
      })),
    markError: (clientId: string) =>
      set((s) => ({
        messages: s.messages.map((m) =>
          m.id === clientId ? {...m, status: 'error'} : m,
        ),
      })),
    addLocal: (local: ChatMessage) =>
      set((s) => ({messages: [...s.messages, local]})),
    removeMessagePart: (messageId: string, partType: string) =>
      set((s) => ({
        messages: s.messages.map((m) =>
          m.id === messageId
            ? {...m, parts: m.parts.filter((p) => p.type !== partType)}
            : m,
        ),
      })),
  }));

  return store;
}
